<template>
  <Topbar />

  <UHeader mode="slideover" toggle-side="left" side="left" :menu="{ side: 'left' }">
    <template #left>
      <CountrySelect />
      <CurrencySelect />
    </template>

    <template #default>
      <Logo />
    </template>

    <template #right>
      <!-- Actions Group -->
      <div class="flex items-center justify-end gap-2">
        <SearchSlideover>
          <UButton variant="outline" size="lg" color="neutral" class="lg:hidden" aria-label="Search">
            <Icon name="i-heroicons-magnifying-glass" class="w-5 h-5" />
          </UButton>
        </SearchSlideover>

        <!-- <UColorModeButton class="px-3" variant="outline" size="lg" color="neutral" /> -->

        <!-- Account button (Desktop) -->
        <UButton v-if="!customerStore.customer && customerStore.loaded" icon="i-heroicons-user" variant="outline" size="lg" :to="$localePath({ name: 'login' })" color="neutral" class="hidden md:inline-flex"
          aria-label="Login">
          <div class="flex flex-col gap-0">
            {{ $t('Login') }}
          </div>
        </UButton>

        <UPopover 
        v-else
          :content="{
            align: 'start',
          }"
        >
        <UButton icon="i-heroicons-user" variant="outline" color="secondary" size="lg"  class="hidden md:inline-flex"
          aria-label="Account">
          <div class="flex flex-col gap-0">
            {{ $t('My account') }}
          </div>
        </UButton>

          <template #content="{ close }">
              <div class="p-2 space-y-1 min-w-[150px]">
        <NuxtLink
          v-for="link in accountLinks"
          :key="link.to"
          :to="link.to"
          @click="close()"
          class="block px-3 py-2 text-sm hover:bg-gray-100 rounded transition-colors"
        >
          {{ link.label }}
        </NuxtLink>
      </div>
          </template>
        

        </UPopover>


        <!-- Wishlist button -->
        <UButton icon="i-heroicons-heart" variant="outline" size="lg" color="neutral" class="hidden md:inline-flex relative"
          @click="$emit('open-wishlist')" aria-label="Wishlist">
          <UBadge
            :class="{
              'hidden': wishlist.count === 0 || wishlist.pending
            }"
          :label="wishlist.count.toString()" size="sm" color="neutral"
            class="absolute -top-1.5 -right-1.5" />
        </UButton>

        <!-- Cart button -->
          <UButton icon="i-heroicons-shopping-bag" variant="outline" size="lg" color="neutral" class="relative" @click="$emit('open-cart')" aria-label="Shopping cart">
            <UBadge :class="{
              'hidden': cart.pending || cart.data.items.length === 0
            }" size='sm'  :label="cart.data.items.length.toString()" color="primary" class="absolute -top-1.5 -right-1.5" />
          </UButton>
      </div>
    </template>

    <!-- Bottom navigation (Desktop)-->
    <template #bottom>
      <div class="bg-default hidden lg:block shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] border-t-1 border-muted">
        <UContainer>
          <nav class="h-12 flex items-center justify-between">
            <CategoriesDropdown />

            <UNavigationMenu :key="locale" :items="navigationItems" />

            <InteractiveProductSearchInput />
          </nav>
        </UContainer>
      </div>
    </template>

    <!-- Mobile content-->
    <template #content="{ close }">
      <div class="p-4 relative flex flex-col h-full">
        <UButton variant="ghost" size="sm" class="absolute top-4 right-4" @click="close">
          <Icon name="i-heroicons-x-mark" class="w-5 h-5" />
        </UButton>

        <Logo />

        <hr class="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700">

        <div class="flex items-center gap-3 mb-4">
          <LocaleSelector />
          <CurrencySelect />
        </div>

        <hr class="h-px my-4 bg-gray-200 border-0 dark:bg-gray-700">

        <div class="grow">
          <UNavigationMenu :items="navigationItems" orientation="vertical"  />
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <UButton variant="outline" color="neutral" :to="$localePath({ name: 'account' })" class="justify-center h-12">
            <Icon name="i-heroicons-user" class="w-5 h-5 mr-2" />
            Account
          </UButton>

          <UButton variant="outline" color="neutral" @click="$emit('open-wishlist'); close && close()" class="justify-center h-12">
            <Icon name="i-heroicons-heart" class="w-5 h-5 mr-2" />
            Wishlist
          </UButton>
        </div>
      </div>
    </template>
  </UHeader>
</template>

<script setup lang="ts">
const { t: $t } = useI18n()

import { useCartStore } from '~/stores/useCartStore'
import { useWishlistStore } from '~/stores/useWishlistStore'
import LocaleSelector from '~/components/atoms/LocaleSelector.vue'
import CurrencySelect from '~/components/atoms/CurrencySelect.vue'
import CategoriesDropdown from '~/components/molecules/CategoriesDropdown.vue'
import Topbar from '~/components/organisms/Topbar.vue'
import Logo from '~/components/atoms/Logo.vue'
import SearchSlideover from '~/components/organisms/SearchSlideover.vue'
import InteractiveProductSearchInput from '~/components/molecules/InteractiveProductSearchInput.vue'


const customerStore = useCustomerStore()

defineEmits<{ (e: 'open-cart'): void; (e: 'open-wishlist'): void }>()

const localePath = useLocalePath()
const { locale } = useI18n()

const catalogService = useCatalogService();


const {
  data
} = await useAsyncData(() => catalogService.getHomepageCollections(), { default: () => ({ data: [] }) })

const collections = computed(() => data.value.data)

const navigationItems = computed(() => [
  [
    {
      label: $t('Catalog'),
      icon: 'i-lucide-shopping-bag',
      defaultOpen: true,
      to: localePath({ name: 'catalog' }),
      children: collections.value.map(collection => ({
        label: collection.name,
        description: collection.description || '',
        icon: 'i-lucide-layers',
        to: localePath({ name: 'catalog-collection', params: { collection: collection.slug } })
      }))
    },
    // {
    //   label: $t('My account'),
    //   icon: 'i-lucide-user',
    //   to: localePath({ name: 'account' }),
    //   children: [
    //     {
    //       label: $t('Orders'),
    //       icon: 'i-lucide-package',
    //       description: $t('Track your orders'),
    //       to: localePath({ name: 'account-orders' })
    //     },
    //     {
    //       label: $t('Wishlist'),
    //       icon: 'i-lucide-heart',
    //       description: $t('Manage your wishlist'),
    //       to: localePath({ name: 'account-wishlist' })
    //     },
    //     {
    //       label: $t('Profile'),
    //       icon: 'i-lucide-user-circle',
    //       description: $t('Manage your account'),
    //       to: localePath({ name: 'account-profile' })
    //     }
    //   ]
    // }
  ],
  [
    {
      label: $t('About'),
      icon: 'i-lucide-info',
      to: localePath({ name: 'about' })
    },
    {
      label: $t('Contact'),
      icon: 'i-lucide-mail',
      to:  localePath({ name: 'contact' })
    }
  ]
])

const accountLinks = computed(() => [
  {
    label: $t('Orders'),
    to: localePath({ name: 'account-orders' })
  },
  {
    label: $t('Profile'),
    to: localePath({ name: 'account-profile' })
  },
  {
    label: $t('Logout'),
    to: localePath({ name: 'logout' })
  }
])

const cart = useCartStore()
const wishlist = useWishlistStore()
</script>